import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import {
  addEmployeeMapping,
  attendanceErrorMessage,
  EmployeeDeviceMapping,
  getEmployeeMappings,
  removeEmployeeMapping,
  updateEmployeeMapping
} from 'src/services/lmsAttendanceService';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import { DirectoryEmployee } from './attendanceOptions';
import SearchableSelect from './SearchableSelect';

type EmployeeMappingProps = {
  employees: DirectoryEmployee[];
  canAdd: boolean;
  canEdit: boolean;
  canRemove: boolean;
  onMessage: (message: string, severity: 'success' | 'error') => void;
};

const EmployeeMapping = ({
  employees,
  canAdd,
  canEdit,
  canRemove,
  onMessage
}: EmployeeMappingProps) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState<EmployeeDeviceMapping[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeDeviceMapping | null>(null);
  const [employeeId, setEmployeeId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [pendingRemove, setPendingRemove] = useState<EmployeeDeviceMapping | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    getEmployeeMappings(page + 1, pageSize)
      .then((res) => {
        if (cancelled) {
          return;
        }
        const data = res?.data?.data;
        setRows(Array.isArray(data?.employees) ? data.employees : []);
        setTotalCount(Number(data?.totalCount) || 0);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setRows([]);
        setTotalCount(0);
        setLoadError(attendanceErrorMessage(error, 'Unable to load employee mappings.'));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, reloadKey]);

  const openAdd = () => {
    setEditing(null);
    setEmployeeId('');
    setDeviceId('');
    setEditorOpen(true);
  };

  const openEdit = (row: EmployeeDeviceMapping) => {
    setEditing(row);
    setEmployeeId(row.employeeId);
    setDeviceId(row.deviceId || '');
    setEditorOpen(true);
  };

  const save = () => {
    const selectedEmployee = employeeId.trim();
    const selectedDevice = deviceId.trim();
    if (!selectedEmployee || !selectedDevice) {
      onMessage('Employee and device id are required.', 'error');
      return;
    }
    dispatch(AppActions.showLoader());
    const request = editing
      ? updateEmployeeMapping(selectedEmployee, selectedDevice)
      : addEmployeeMapping(selectedEmployee, selectedDevice);
    request
      .then(() => {
        setEditorOpen(false);
        onMessage(
          editing ? 'Employee mapping updated.' : 'Employee mapping added.',
          'success'
        );
        setReloadKey((value) => value + 1);
      })
      .catch((error) => {
        onMessage(attendanceErrorMessage(error, 'Unable to save the employee mapping.'), 'error');
      })
      .finally(() => dispatch(AppActions.hideLoader()));
  };

  const confirmRemove = () => {
    if (!pendingRemove) {
      return;
    }
    dispatch(AppActions.showLoader());
    removeEmployeeMapping(pendingRemove.employeeId)
      .then(() => {
        setPendingRemove(null);
        onMessage('Employee mapping removed.', 'success');
        if (rows.length === 1 && page > 0) {
          setPage((current) => current - 1);
        } else {
          setReloadKey((value) => value + 1);
        }
      })
      .catch((error) => {
        onMessage(attendanceErrorMessage(error, 'Unable to remove the employee mapping.'), 'error');
      })
      .finally(() => dispatch(AppActions.hideLoader()));
  };

  const mappedIds = new Set(rows.map((row) => row.employeeId));
  const employeeOptions = employees
    .filter((employee) => editing || !mappedIds.has(employee.id))
    .map((employee) => ({ id: employee.id, label: employee.name }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {canAdd && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add
          </Button>
        )}
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#F4F6FB' }}>
            <TableCell sx={{ fontSize: 11 }}>EMPLOYEE NAME</TableCell>
            <TableCell sx={{ fontSize: 11 }}>DEVICE ID</TableCell>
            {(canEdit || canRemove) && <TableCell sx={{ fontSize: 11 }} align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Loading employee mappings...
              </TableCell>
            </TableRow>
          ) : loadError ? (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'error.main' }}>
                {loadError}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No employee mappings yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.employeeId} hover>
                <TableCell>
                  <Typography variant="h5">{row.employeeName || '-'}</Typography>
                </TableCell>
                <TableCell>{row.deviceId}</TableCell>
                {(canEdit || canRemove) && (
                  <TableCell align="right">
                    {canEdit && (
                      <IconButton aria-label="Edit mapping" onClick={() => openEdit(row)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canRemove && (
                      <IconButton aria-label="Remove mapping" onClick={() => setPendingRemove(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Edit employee mapping' : 'Map employee'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Employee
              </Typography>
              {editing ? (
                <TextField size="small" fullWidth value={editing.employeeName} disabled />
              ) : (
                <SearchableSelect
                  value={employeeId || 'all'}
                  allLabel="Select employee"
                  options={employeeOptions}
                  onChange={(id) => setEmployeeId(id === 'all' ? '' : id)}
                />
              )}
            </Box>
            <TextField
              label="Device ID"
              size="small"
              fullWidth
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setEditorOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            {editing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        isLoading={false}
        open={Boolean(pendingRemove)}
        toggle={() => setPendingRemove(null)}
        noToggle={() => setPendingRemove(null)}
        message={`Are you sure you want to remove this employee mapping?`}
        onConfirm={confirmRemove}
      />
    </Box>
  );
};

export default EmployeeMapping;
